import { FC } from 'react';
import styles from './SamplePlanTable.module.scss';
import {
  SampleDefinitionRowData,
  optionSet,
  TableOptions,
  useSamplePlanTableContext,
  useOpenSection,
} from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { IndeterminateCheckbox } from '@/components/UI/Table/Columns/Selector';
import { ChevronCell } from './ChevronCell';
import { DetailCell } from './DetailCell';
import { MetadataCell } from './MetadataCell';
import { CalculateTimepointCell } from './CalculateTimepointCell';
import { GroupRows } from './GroupRows';

export const SampleDefinitionRow: FC<{ data: SampleDefinitionRowData }> = ({ data }) => {
  const { active_metadata, active_details, sample_definition_counts, table_options, selection, updateRowSelection } =
    useSamplePlanTableContext();
  const groupsOpen = useOpenSection('sample_definition', data.api_id);
  const counts = sample_definition_counts.get(data.api_id) ?? {
    planned: 0,
    registered: 0,
    capacity: 0,
  };

  return (
    <>
      <div
        className={styles.SampleDefinitionRow}
        data-test-component="SampleDefinitionRow"
        data-test-element="container"
      >
        {optionSet(TableOptions.display_selection_column, table_options) ? (
          <div>
            <IndeterminateCheckbox
              checked={selection.has(data.api_id)}
              onChange={() => updateRowSelection({ type: 'toggle', value: data.api_id })}
              className="ui-table-selector"
              data-test-component="IndeterminateCheckbox"
              data-test-element="checkbox-input"
            />
          </div>
        ) : null}
        <ChevronCell
          type="sample_definition"
          id={data.api_id}
          disabled={data.target.type === 'no_target'}
          className="f5"
          data-test-component="ChevronCell"
          data-test-element="sample-definition"
        />
        <div>{data.name}</div>
        <div>{data.group_numbers.join(', ')}</div>
        {optionSet(TableOptions.display_planned_animal_column, table_options) ? (
          <div>{data.target.type === 'groups' ? `${counts.capacity}` : `${counts.planned}`}</div>
        ) : null}
        {optionSet(TableOptions.display_registered_animal_column, table_options) ? (
          <div>
            {data.target.type === 'groups' || data.target.type === 'planned_animals'
              ? `${counts.registered} / ${counts.capacity}`
              : `${counts.registered}`}
          </div>
        ) : null}
        {optionSet(TableOptions.collected_samples_count, table_options) ? (
          <>
            <div>{data.collected_samples_count?.totals.planned ?? 0}</div>
            <div>{data.collected_samples_count?.totals.collected ?? 0}</div>
          </>
        ) : null}
        <div>{data.sample_type_name}</div>
        <div>{data.timepoints}</div>
        {optionSet(TableOptions.hide_calculate_collection_time_column, table_options) ? null : (
          <div>
            <CalculateTimepointCell value={data.calculate_collection_time} />
          </div>
        )}
        {Array.from(active_details).map((detail) => (
          <DetailCell key={detail} detail={data.details?.[detail]} />
        ))}
        {Array.from(active_metadata).map((api_id) => (
          <MetadataCell key={api_id} type={api_id} value={data.metadata?.[api_id]} />
        ))}
      </div>
      {groupsOpen ? <GroupRows data={data} /> : null}
    </>
  );
};
